import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {Autoplay, Pagination} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {emitEventLog} from '@di-shared/features/analytics';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import DrivingDistanceSectionNa from '@di-shared/features/home/na/driving/DrivingDistanceSection.na';
import FuelEfficiencySectionNa from '@di-shared/features/home/na/driving/FuelEfficiencySection.na';
import SafeDrivingSectionLN from '@di-shared/features/home/na/driving/SafeDrivingSectionLN.na';
import useAvailableComponentNa from '@di-shared/features/home/na/useAvailableComponent.na';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import {BrandType} from '@shared/cci/features/const/environment';

interface DrivingContentsNaProps {
  vin: string;
}

function DrivingContentsNa({vin}: DrivingContentsNaProps) {
  const {t} = useInferableTranslation();
  return (
    <ErrorBoundary fallback={<></>}>
      <Suspense
        fallback={
          <section className="section">
            {import.meta.env.VITE_BRAND === BrandType.KIA && <SafeDrivingSectionLN.Loading />}
            <section className="section section_trip_history">
              <div className="trip_history_grid">
                <div className="trip_history_grid_item">
                  <div className="driving_banner type_block">
                    <div className="driving_banner_box skeleton">
                      <span className="driving_banner_txtbox">
                        <strong
                          className="driving_banner_title body_medium_b"
                          style={{whiteSpace: 'pre-wrap'}}
                        >
                          {t('msg_home_introduction_01')}
                        </strong>
                        <span className="driving_banner_desc body_small_r">{t('msg_home_introduction_02')}</span>
                      </span>
                    </div>
                  </div>
                  <FuelEfficiencySectionNa.Loading />
                </div>
                <div className="trip_history_grid_item">
                  <DrivingDistanceSectionNa.Loading />
                  <div className="driving_banner type_block skeleton">
                    <div className="driving_banner_box">
                      <span className="driving_banner_txtbox type_reverse">
                        <strong className="driving_banner_title body_medium_b">{t('msg_home_knowingthedata_01')}</strong>
                        <span className="driving_banner_desc body_small_r">{t('msg_home_knowingthedata_02')}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </section>
        }
      >
        <Inner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: DrivingContentsNaProps) {
  const {t} = useInferableTranslation();
  const {checkAvailableComponent} = useAvailableComponentNa({vin});
  const resolveFeatureFlag = useFirebaseStore((state) => state.resolveFeatureFlag);
  const isSafetyScoreAvailable = checkAvailableComponent('SAFETY_SCORE');

  return (
    <section className="section">
      {/* 안전운전점수 */}
      {resolveFeatureFlag('NA_SAFETY_SCORE', vin, {serverSpecVisible: isSafetyScoreAvailable}) &&
        import.meta.env.VITE_BRAND === BrandType.KIA && <SafeDrivingSectionLN vin={vin} />}

      {/* 서비스 소개 배너 (Swiper) + 주행 정보 */}
      <div className="section section_trip_history">
        <div className="trip_history_grid">
          <div className="trip_history_grid_item">
            {/* Service Introduction 배너 — Swiper */}
            <Swiper
              slidesPerView={'auto'}
              className={'block-banner-swiper'}
              modules={[Pagination, Autoplay]}
              autoplay={{delay: 5000, disableOnInteraction: false}}
              pagination={{el: '.swiper-pagination', type: 'bullets'}}
              spaceBetween={18}
              observer
              observeParents
              loop
            >
              <SwiperSlide>
                <div
                  className="driving_banner type_block"
                  style={{
                    backgroundColor:
                      import.meta.env.VITE_BRAND === BrandType.GENESIS
                        ? 'var(--primary-fixed-ultimate)'
                        : import.meta.env.VITE_BRAND === BrandType.HYUNDAI
                          ? 'var(--di-point-back)'
                          : '',
                  }}
                >
                  <LinkTo
                    className="driving_banner_box"
                    to={ROUTE_PATHS.ABOUT.getFullPath()}
                    onClickBypass={() =>
                      emitEventLog({
                        event: 'di_driveinfo_interaction',
                        data_action: 'view banner',
                        data_name: 'Driving Insight Introduction',
                      })
                    }
                  >
                    <span className="driving_banner_txtbox">
                      <strong
                        className="driving_banner_title body_medium_b"
                        style={{whiteSpace: 'pre-wrap'}}
                      >
                        {t('msg_home_introduction_01')}
                      </strong>
                      <p className="driving_banner_desc body_small_r">{t('msg_home_introduction_02')}</p>
                    </span>
                    <span className="driving_banner_imgbox">
                      <ImageSelector
                        className="driving_banner_img"
                        alt="driving_banner_img"
                        kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_eu_home_intro_block_light.png')}
                        kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_eu_home_intro_block.png')}
                        hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eu_home_intro_block.png')}
                        hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eu_home_intro_block.png')}
                        genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eu_home_intro_block.png')}
                        genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eu_home_intro_block.png')}
                      />
                    </span>
                  </LinkTo>
                </div>
              </SwiperSlide>
              <div className="swiper-pagination" />
            </Swiper>

            {/* 평균 연비 */}
            {checkAvailableComponent('TRIPS') && (
              <ErrorBoundary fallback={<FuelEfficiencySectionNa.Error />}>
                <Suspense fallback={<FuelEfficiencySectionNa.Loading />}>
                  <FuelEfficiencySectionNa vin={vin} />
                </Suspense>
              </ErrorBoundary>
            )}
          </div>
          <div className="trip_history_grid_item">
            {/* 평균 주행 */}
            {checkAvailableComponent('TRIPS') && (
              <ErrorBoundary fallback={<DrivingDistanceSectionNa.Error />}>
                <Suspense fallback={<DrivingDistanceSectionNa.Loading />}>
                  <DrivingDistanceSectionNa vin={vin} />
                </Suspense>
              </ErrorBoundary>
            )}

            {/* 데이터 알아보기 배너 */}
            <div className="driving_banner type_block">
              <LinkTo
                className="driving_banner_box"
                to={ROUTE_PATHS.ABOUT_DATA_EXPLORE.getFullPath()}
                onClickBypass={() =>
                  emitEventLog({
                    event: 'di_driveinfo_interaction',
                    data_action: 'view banner',
                    data_name: 'Knowing the Data',
                  })
                }
              >
                <span className="driving_banner_txtbox type_reverse">
                  <strong
                    className="driving_banner_title body_medium_b"
                    style={{whiteSpace: 'pre-wrap'}}
                  >
                    {t('msg_home_knowingthedata_02')}
                  </strong>
                  <span className="driving_banner_desc body_small_r">{t('msg_home_knowingthedata_01')}</span>
                </span>
                <span className="driving_banner_imgbox">
                  <ImageSelector
                    className="driving_banner_img"
                    alt="driving_banner_img"
                    kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_bar_graph_block_light.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_bar_graph_block.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_bar_graph_block_light.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_bar_graph_block.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_bar_graph_block_light.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_bar_graph_block.png')}
                  />
                </span>
              </LinkTo>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default DrivingContentsNa;
